import React, { useEffect, useState } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import SplashScreen from '@/screens/SplashScreen';
import HomeScreen from '@/screens/HomeScreen';
import PlayHubScreen from '@/screens/PlayHubScreen';
import GameScreen from '@/screens/GameScreen';
import SettingsScreen from '@/screens/SettingsScreen';
import OnlineLobbyScreen from '@/screens/OnlineLobbyScreen';
import OnlineGameScreen from '@/screens/OnlineGameScreen';
import GuestNameModal from '@/components/GuestNameModal';
import { loadOnlineSession } from '@/net/onlineSession';
import { sounds } from '@/audio/sounds';
import { storage } from '@/lib/storage';
import { ensureGuest, hasDisplayName, recoverDisplayNameFromServer } from '@/lib/guestIdentity';
import { track, EVT } from '@/lib/telemetry';
import { installSessionLifecycle } from '@/lib/sessionTracking';
import AdminAnalyticsScreen from '@/screens/AdminAnalyticsScreen';
import ProfileScreen from '@/screens/ProfileScreen';
import MatchReportScreen from '@/screens/MatchReportScreen';
import MatchSummaryRoute from '@/screens/MatchSummaryRoute';
import DesignPreviewV5B from '@/screens/DesignPreviewV5B';
import RankedLobbyScreen from '@/screens/RankedLobbyScreen';
import RankedSearchScreen from '@/screens/RankedSearchScreen';
import RankedLeaderboardScreen from '@/screens/RankedLeaderboardScreen';
import WeeklyCupScreen from '@/screens/WeeklyCupScreen';

function Root() {
  const navigate = useNavigate();
  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => {
    sounds.setEnabled(storage.getSettings().soundEnabled);
  }, []);

  if (!splashDone) {
    return <SplashScreen onDone={() => { setSplashDone(true); navigate('/home', { replace: true }); }} />;
  }
  return (
    <HomeScreen
      onPlay={() => navigate('/play-hub')}
      onPlayOnline={() => navigate('/play-hub')}
      onTutorial={() => {
        // "How to Play · 60 sec" → re-arm the primer and drop the user into a fresh bot match.
        storage.setSettings({ tutorialPrimerSeen: false, tutorialSkipped: false });
        navigate('/play');
      }}
      onSettings={() => navigate('/settings')}
      onProfile={() => navigate('/profile/me')}
      onLeaderboard={() => navigate('/ranked/leaderboard')}
      onWeeklyCup={() => navigate('/ranked/weekly-cup')}
    />
  );
}

function HomeRoute() {
  const navigate = useNavigate();
  return (
    <HomeScreen
      onPlay={() => navigate('/play-hub')}
      onPlayOnline={() => navigate('/play-hub')}
      onTutorial={() => {
        storage.setSettings({ tutorialPrimerSeen: false, tutorialSkipped: false });
        navigate('/play');
      }}
      onSettings={() => navigate('/settings')}
      onProfile={() => navigate('/profile/me')}
      onLeaderboard={() => navigate('/ranked/leaderboard')}
      onWeeklyCup={() => navigate('/ranked/weekly-cup')}
    />
  );
}

// PHASE 5.0A — Play Hub route. Pass 1C: modes are wired to their
// existing matchmaking / room / bot actions DIRECTLY from within
// PlayHubScreen (via the shared `useOnlineActions` hook and inline
// `MatchmakingScreen`). No more mode-picker detour through /online.
function PlayHubRoute() {
  const navigate = useNavigate();
  return <PlayHubScreen onBack={() => navigate('/home')} />;
}

function OnlineRoute() {
  const navigate = useNavigate();
  // Auto-restore any recent online session (tab reload / mobile suspend
  // within 60 s of being last saved). The OnlineSocket on mount will
  // present the same token to the server and re-attach as the same
  // player, picking up exactly where the match was paused.
  const [session, setSession] = useState(() => loadOnlineSession());
  if (session) {
    return <OnlineGameScreen session={session} onLeave={() => { setSession(null); navigate('/home'); }} />;
  }
  return (
    <OnlineLobbyScreen
      onEnterMatch={(s) => setSession(s)}
      onBack={() => navigate('/home')}
      onPlayBot={() => navigate('/play')}
    />
  );
}

function SettingsRoute() {
  const navigate = useNavigate();
  return <SettingsScreen onClose={() => navigate('/home')} />;
}

// PHASE 3.13.3 — the /stats route now redirects to /profile/me. Profile
// is the single stats destination; local Play-vs-Bot totals are shown as
// a card inside Profile > Modes. Old bookmarks continue to work.

function App() {
  // Phase 3.9A + 3.10 — bypass guest gate for admin & public profile routes.
  const _isAdmin = () => typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname.startsWith('/admin/');
  const _isProfile = () => typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname.startsWith('/profile/') && !window.location.pathname.endsWith('/me');
  const _isMatchReport = () => typeof window !== 'undefined' && window.location && window.location.pathname && window.location.pathname.startsWith('/match-report/');
  // Phase 4.1.0 — leaderboard is a public read-only surface. Allow anonymous viewing.
  // Phase 4.2.0 — Weekly Cup is also a public read-only surface.
  const _isRankedLeaderboard = () => typeof window !== 'undefined' && window.location && window.location.pathname && (window.location.pathname === '/ranked/leaderboard' || window.location.pathname === '/ranked/weekly-cup');
  const isAdminPath = _isAdmin;
  const isPublicProfilePath = _isProfile;
  const isMatchReportPath = _isMatchReport;
  const isRankedLeaderboardPath = _isRankedLeaderboard;

  // Phase 3.1 — guest identity gate. Run BEFORE the router so any
  // first-time visitor is asked to choose a name once. ensureGuest()
  // also migrates the old lobby/session name automatically.
  //
  // Phase 3.8.1 — Browser → PWA continuity: if the PWA boot found a
  // guestId in the first-party cookie (set by the in-browser visit)
  // but localStorage is fresh, we fetch the server profile and adopt
  // the canonical displayName silently so the player is NOT forced
  // back to the GuestNameModal.
  const [guestReady, setGuestReady] = useState(false);
  useEffect(() => {
    // PHASE 4.1.3 — Production stability gate. Print BOTH the backend
    // build stamp and the frontend build id to the console on boot so
    // users / support can eyeball whether the freshly-deployed code is
    // actually running (fixes the "old bundle served from cache"
    // failure mode reported for ranked-queue latency).
    // eslint-disable-next-line no-console
    console.info(
      `%c[Kickard] frontend build=${process.env.REACT_APP_BUILD_ID || 'dev'} phase=4.1.3`,
      'color:#f4c430;font-weight:600',
    );
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
      if (BACKEND) {
        fetch(`${BACKEND}/api/version`, { cache: 'no-store' })
          .then((r) => (r.ok ? r.json() : null))
          .then((v) => {
            if (!v) return;
            // eslint-disable-next-line no-console
            console.info(
              `%c[Kickard] backend phase=${v.phase} sha=${v.gitSha || '?'} `
              + `boot=${v.backendBootAt || '?'} pod=${v.pod || '?'}`,
              'color:#f4c430;font-weight:600',
            );
            // Also expose for support / DevTools inspection.
            try { window.__KICKARD_VERSION__ = { frontend: process.env.REACT_APP_BUILD_ID || 'dev', backend: v }; } catch (_) { /* ignore */ }
          })
          .catch(() => {});
      }
    } catch (_) { /* ignore */ }
    // Phase 3.9A + 3.10 + 3.13 + 4.1.0 — skip guest gate + session tracking on admin, public profile, match-report and public leaderboard routes.
    if (isAdminPath() || isPublicProfilePath() || isMatchReportPath() || isRankedLeaderboardPath()) {
      setGuestReady(true);
      return;
    }
    let cancelled = false;
    (async () => {
      ensureGuest();
      // If localStorage didn't carry a displayName but a guestId did
      // come back from the cookie, try to recover the server-canonical
      // name. Non-blocking for ~250 ms then we proceed regardless.
      if (!hasDisplayName()) {
        try { await recoverDisplayNameFromServer(); } catch (_) { /* ignore */ }
      }
      if (cancelled) return;
      setGuestReady(hasDisplayName());
      track(EVT.APP_OPEN);
      // Phase 3.9A — session tracking install once identity is ready.
      try { installSessionLifecycle('home'); } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="App">
      <div className="landscape-warning">Please rotate to portrait to play Kickard</div>
      {/*
        Phase 3.9A — bypass the player guest gate for internal admin routes.
        Admin analytics has its own key gate; it does NOT need a Kickard
        displayName to render.
      */}
      {!guestReady && !isAdminPath() && !isPublicProfilePath() && !isMatchReportPath() && !isRankedLeaderboardPath() && (
        <GuestNameModal mode="first" onSaved={() => setGuestReady(true)} />
      )}
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Root />} />
          <Route path="/home" element={<HomeRoute />} />
          <Route path="/play-hub" element={<PlayHubRoute />} />
          <Route path="/play" element={<GameScreen />} />
          <Route path="/online" element={<OnlineRoute />} />
          <Route path="/settings" element={<SettingsRoute />} />
          <Route path="/stats" element={<Navigate to="/profile/me" replace />} />
          {/*
            Phase 3.9A — internal admin analytics dashboard.
            DELIBERATELY NOT linked from any player-facing UI, not in the
            sitemap, and not referenced by robots.txt. Access requires
            manual URL entry + ADMIN_ANALYTICS_KEY (backend-side).
          */}
          <Route path="/admin/analytics" element={<AdminAnalyticsScreen />} />
          {/* Phase 3.10 — Public player profiles. */}
          <Route path="/profile/me" element={<ProfileScreen />} />
          <Route path="/profile/:profileId" element={<ProfileScreen />} />
          {/* Phase 3.13 — Luck Report. */}
          <Route path="/match-report/:matchId" element={<MatchReportScreen />} />
          {/* PHASE 5.0A PASS 3A — Read-only Match Summary restoration
              route. Rendered ONLY as the Back destination from Luck
              Report when origin === 'match_summary'. Reads a
              sessionStorage snapshot; no live socket / room state. */}
          <Route path="/match-summary/:matchId" element={<MatchSummaryRoute />} />
          {/* PHASE 5.0B-1 — internal visual foundation preview (not
              linked from Home or navigation; discoverable by URL). */}
          <Route path="/design/v5b" element={<DesignPreviewV5B />} />
          {/* Phase 4.0.4 — Ranked frontend (feature-flagged at runtime). */}
          <Route path="/ranked" element={<RankedLobbyScreen />} />
          <Route path="/ranked/search" element={<RankedSearchScreen />} />
          {/* Phase 4.1.0 — Public Ranked Leaderboard. */}
          <Route path="/ranked/leaderboard" element={<RankedLeaderboardScreen />} />
          <Route path="/ranked/weekly-cup" element={<WeeklyCupScreen />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
